import React, { useState, useEffect } from 'react';
import { Card, FormTextarea, Button, Select } from '../../../../components/ui';
import { SectionTitle } from '../../../../components/ui/Typography';
import api from '../../../../services/api';
import toast from 'react-hot-toast';

export const InitialQuotationApprovalForm = ({ initialData = {}, onSubmit, onCancel, loading = false, hideActions = false, commonNotes, notesValue }) => {
  const [employees, setEmployees] = useState([]);
  const [fetching, setFetching] = useState(true);

  const [formData, setFormData] = useState({
    notes: notesValue || initialData?.initialQuotationApproval?.requestNotes || '',
    designerUser: initialData?.initialQuotationApproval?.designerUser || "",
    managerUser: initialData?.initialQuotationApproval?.managerUser || "",
    ownerUser: initialData?.initialQuotationApproval?.ownerUser || "",
  });

  const isFullyApproved = initialData?.initialQuotationApproval?.approvalStatus === 'approved';

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/teams?limit=1000&includeOwner=true");
        const users = res.data?.data || res.data || [];
        setEmployees(users);

        const owner = users.find((u) => u.role?.toLowerCase() === "owner");
        if (owner) {
          setFormData((prev) => ({ ...prev, ownerUser: owner._id }));
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load team members");
      } finally {
        setFetching(false);
      }
    };
    fetchEmployees();
  }, []);

  const designers = employees.filter((user) => user.role?.toLowerCase() === "designer");
  const projectManagers = employees.filter((user) => user.role?.toLowerCase() === "project-manager");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Card padding="md" className="animate-in fade-in slide-in-from-bottom-2">
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex flex-col">
            <SectionTitle>Initial Quotation Approval</SectionTitle>
          </div>
        </div>

        <div className="border border-green-200 bg-green-50/30 rounded-xl p-5 shadow-sm mt-4">
          <div className="flex items-center gap-2 mb-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <h3 className="text-[12px] font-bold text-slate-700 uppercase tracking-widest">
              THREE-LAYER APPROVAL WORKFLOW
            </h3>
          </div>

          <p className="text-[13px] text-slate-500 mb-6">
            Please assign a team member for each level of the approval process.
            The quotation will be routed to them sequentially.
          </p>

          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4 relative">
              {/* Connecting line */}
              <div className="absolute top-4 left-0 w-full h-[1px] bg-slate-200 z-0 hidden sm:block"></div>

              {/* Step 1: Designer */}
              <div className="flex-1 flex flex-col gap-3 relative z-10 bg-green-50/30 sm:bg-transparent px-2 sm:px-0">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-sm border-2 border-white mx-auto shadow-sm">
                  1
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                    Designer <span className="text-slate-400 font-normal text-[10px] ml-1">(Optional)</span>
                  </label>
                  <Select
                    name="designerUser"
                    value={formData.designerUser}
                    onChange={handleChange}
                    className="w-full text-sm"
                    disabled={fetching || isFullyApproved}
                  >
                    <option value="">Select Designer</option>
                    {designers.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name || `${emp.firstName} ${emp.lastName || ""}`} (Designer)
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Step 2: Project Manager */}
              <div className="flex-1 flex flex-col gap-3 relative z-10 bg-green-50/30 sm:bg-transparent px-2 sm:px-0">
                <div className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-600 font-bold flex items-center justify-center text-sm border-2 border-white mx-auto shadow-sm">
                  2
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                    Project Manager <span className="text-slate-400 font-normal text-[10px] ml-1">(Optional)</span>
                  </label>
                  <Select
                    name="managerUser"
                    value={formData.managerUser}
                    onChange={handleChange}
                    className="w-full text-sm"
                    disabled={fetching || isFullyApproved}
                  >
                    <option value="">Select Manager</option>
                    {projectManagers.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name || `${emp.firstName} ${emp.lastName || ""}`} (Project Manager)
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Step 3: Owner */}
              <div className="flex-1 flex flex-col gap-3 relative z-10 bg-green-50/30 sm:bg-transparent px-2 sm:px-0">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 font-bold flex items-center justify-center text-sm border-2 border-white mx-auto shadow-sm">
                  3
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                    Owner
                  </label>
                  <input
                    type="text"
                    disabled
                    value={employees.find((user) => user.role?.toLowerCase() === "owner")?.name || "No Owner Found"}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {commonNotes ? commonNotes : (
          <FormTextarea
            label="Approval Notes"
            name="notes"
            placeholder="Any specific comments from the customer..."
            value={formData.notes || ''}
            onChange={handleChange}
            rows={3}
            size="sm"
            labelClassName="label text-xs"
            inputClassName="input text-sm"
          />
        )}

        {!hideActions && (
          <div className="flex gap-3 pt-6 mt-6 border-t border-slate-50">
            {/* update Halimunnisa 09-06-2026
            The approval button is disabled if the quotation has already been fully approved, preventing duplicate submissions. The button text also changes to "Already Approved" to provide clear feedback to the user about the current status of the quotation. */}
            <Button
              type="button"
              onClick={handleSubmit}
              loading={loading}
              className="!bg-slate-900 hover:!bg-slate-800 !text-white !border-none flex-1 py-2.5 flex items-center justify-center gap-2 rounded-lg shadow-md"
              disabled={isFullyApproved}
            >
              {isFullyApproved ? "Already Approved" : "Request Approval"}
            </Button>
            {onCancel && (
              <Button
                onClick={onCancel}
                variant="secondary"
                className="px-6 py-2.5 text-slate-600 border-slate-200 hover:bg-transparent hover:text-slate-800 focus:bg-transparent active:bg-transparent shadow-none"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default InitialQuotationApprovalForm;
